import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * while preserving server-rendered line restrictions.
   */
  updateButtonStates() {
    const { minusButton, plusButton, minusButtonLabel } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const canRemoveAtOne = this.dataset.canRemove === 'true' && min === 1 && value === 1;

    minusButton.disabled = this.serverDisabledMinus || (value <= min && !canRemoveAtOne);
    plusButton.disabled = this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
    if (minusButtonLabel) {
      minusButtonLabel.textContent = canRemoveAtOne ? this.dataset.removeLabel : this.dataset.decreaseLabel;
    }
  }

  /**
   * Removes an eligible cart line when minus is pressed at quantity one.
   * Product selectors keep the base class minimum behavior.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const { min, value } = this.getCurrentValues();
    const canRemoveAtOne = this.dataset.canRemove === 'true' && !this.serverDisabledMinus && min === 1 && value === 1;

    if (!canRemoveAtOne) {
      super.decreaseQuantity(event);
      return;
    }

    event.preventDefault();
    this.refs.quantityInput.value = '0';
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
